/**
 * Better Auth JWT claim decoding for the browser (goal 047; trimmed to the
 * client half by plan 06).
 *
 * Product has no server runtime any more: the SPA holds the Better Auth session
 * cookie, the auth Worker mints the JWT, and ProductData is the cryptographic
 * verifier. What survives here is the read-only claim decoding this app does on
 * its own copy of that token — nothing here is an auth decision.
 *
 * The server-side seams (`fetchBetterAuthSession`, `getBetterAuthBearerToken`,
 * `resolveAuthServiceBase`, the forwarded-host helpers and the token cache)
 * went with the Next.js API tree; the Worker's `src/worker/auth-proxy.ts` is what
 * forwards `/api/auth/*` now, and it carries its own cookie reader.
 */
import { AUTH_SECURITY_CUTOVER_VERSION, hasCurrentAuthSecurityCutover } from '@nt/auth-contracts'

function base64UrlDecode(segment: string): string | null {
  try {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
    return atob(padded)
  } catch {
    return null
  }
}

export interface CurrentAuthJwtClaims {
  expMs: number | null
  /** The Better Auth user id the token was minted for; null when absent. */
  sub: string | null
  /** Null when the minting Worker predates the cutover. Recorded, not required. */
  authSecurityCutover: typeof AUTH_SECURITY_CUTOVER_VERSION | null
}

/**
 * Decode the JWT claims needed by Product WITHOUT verifying the signature.
 * ProductData remains the cryptographic verifier.
 */
export function decodeCurrentAuthJwtClaims(token: string): CurrentAuthJwtClaims | null {
  const parts = token.split('.')
  if (parts.length !== 3) return null
  const decoded = base64UrlDecode(parts[1])
  if (!decoded) return null
  try {
    const payload = JSON.parse(decoded) as { exp?: unknown; sub?: unknown; authSecurityCutover?: unknown }
    // The cutover claim is recorded, NOT required. Production pins
    // AUTH_MIGRATIONS_ENABLED to "false", so the cutover never runs there and
    // the claim is only a marker of which auth Worker minted the token — no
    // per-user security property. Rejecting tokens without it bought nothing
    // and made this app reject every JWT the currently deployed Worker mints,
    // i.e. a total authenticated outage for the whole deploy window.
    // Verification is still enforced: `emailVerified` in ProductData, plus the
    // revocation trigger in the auth Worker.
    return {
      expMs: typeof payload.exp === 'number' && Number.isFinite(payload.exp) ? payload.exp * 1000 : null,
      sub: typeof payload.sub === 'string' && payload.sub.length > 0 ? payload.sub : null,
      authSecurityCutover: hasCurrentAuthSecurityCutover(payload) ? AUTH_SECURITY_CUTOVER_VERSION : null,
    }
  } catch {
    return null
  }
}
